import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { Panel, Empty } from "./Panel";
import { C, PALETTE, fmtPeriod } from "./format";

const axis = { fontSize: 11, fill: C.muted, fontFamily: "IBM Plex Mono, monospace" };
const tip = { contentStyle: { border: `1px solid ${C.border}`, borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Sans" }, cursor: { fill: "#F5F3E9" } };

export function TrendChart({ data, gran }) {
  const rows = data.map((r) => ({ ...r, label: fmtPeriod(r.period, gran) }));
  const any = rows.some((r) => r.created || r.closed || r.moved);
  return (
    <Panel title="Volume trend" subtitle={`Created vs closed, per ${gran}`} testid="trend-chart" className="lg:col-span-8">
      {!any ? <Empty text="No file activity in this period." /> : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.navy} stopOpacity={0.25} /><stop offset="100%" stopColor={C.navy} stopOpacity={0} /></linearGradient>
                <linearGradient id="gClosed" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.teal} stopOpacity={0.3} /><stop offset="100%" stopColor={C.teal} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#EFEEE5" vertical={false} />
              <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: C.border }} minTickGap={16} />
              <YAxis allowDecimals={false} tick={axis} tickLine={false} axisLine={false} />
              <Tooltip {...tip} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="created" name="Created" stroke={C.navy} strokeWidth={2} fill="url(#gCreated)" />
              <Area type="monotone" dataKey="closed" name="Closed" stroke={C.teal} strokeWidth={2} fill="url(#gClosed)" />
              <Line type="monotone" dataKey="moved" name="Movements" stroke={C.orange} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export function TurnaroundChart({ data }) {
  const any = data.some((d) => d.count);
  const colors = [C.teal, C.teal, C.navy, C.orange, C.red];
  return (
    <Panel title="Turnaround time" subtitle="Files closed this period, by days taken" testid="turnaround-chart" className="lg:col-span-4">
      {!any ? <Empty text="No files closed in this period." /> : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid stroke="#EFEEE5" vertical={false} />
              <XAxis dataKey="bucket" tick={{ ...axis, fontSize: 10 }} tickLine={false} axisLine={{ stroke: C.border }} interval={0} />
              <YAxis allowDecimals={false} tick={axis} tickLine={false} axisLine={false} />
              <Tooltip {...tip} />
              <Bar dataKey="count" name="Files" radius={0}>
                {data.map((_, i) => <Cell key={i} fill={colors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export function DepartmentChart({ data }) {
  return (
    <Panel title="By department" subtitle="Open vs closed · all time" testid="department-chart" className="lg:col-span-7">
      {!data.length ? <Empty text="No files yet." /> : (
        <div style={{ height: Math.max(200, data.length * 34 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="#EFEEE5" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={axis} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ ...axis, fill: C.soft }} tickLine={false} axisLine={false} />
              <Tooltip {...tip} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="open" name="Open" stackId="a" fill={C.navy} barSize={16} />
              <Bar dataKey="closed" name="Closed" stackId="a" fill={C.teal} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export function CategoryChart({ data }) {
  const rows = data.map((d) => ({ name: d.name, value: d.open + d.closed }));
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <Panel title="By category" subtitle="Share of all files · all time" testid="category-chart" className="lg:col-span-5">
      {!total ? <Empty text="No files yet." /> : (
        <div className="flex items-center gap-4 h-full">
          <div className="relative w-[170px] h-[170px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="value" innerRadius={52} outerRadius={80} paddingAngle={1} stroke="#fff">
                  {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip {...tip} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-heading text-[26px] font-bold leading-none">{total}</div>
              <div className="font-mono-eo text-[9px] uppercase tracking-widest text-[#8A8A80] mt-1">files</div>
            </div>
          </div>
          <ul className="flex-1 min-w-0 space-y-1.5">
            {rows.map((r, i) => (
              <li key={r.name} className="flex items-center gap-2 text-[12px]" data-testid={`category-row-${i}`}>
                <span className="w-2.5 h-2.5 shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                <span className="truncate flex-1 text-[#3A3A32]" title={r.name}>{r.name}</span>
                <span className="font-mono-eo text-[11px] text-[#5C5C54]">{r.value}</span>
                <span className="font-mono-eo text-[10px] text-[#8A8A80] w-9 text-right">{Math.round((r.value / total) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}
